/**
 * 数独题目生成。
 * 先填出一盘终局，再按难度挖空；每挖一格都确认剩余盘面仍只有一个解。
 */

export type DifficultyKey = 'easy' | 'medium' | 'hard' | 'expert'

const HOLES: Record<DifficultyKey, number> = {
  easy: 32,
  medium: 42,
  hard: 50,
  expert: 56,
}

const ALL = 0x1ff

const boxIndex = (r: number, c: number) => Math.floor(r / 3) * 3 + Math.floor(c / 3)

const bitCount = (mask: number) => {
  let n = 0
  while (mask) {
    n++
    mask &= mask - 1
  }
  return n
}

interface Masks {
  rows: number[]
  cols: number[]
  boxes: number[]
}

const emptyMasks = (): Masks => ({
  rows: Array(9).fill(0),
  cols: Array(9).fill(0),
  boxes: Array(9).fill(0),
})

const place = (masks: Masks, r: number, c: number, bit: number) => {
  masks.rows[r] |= bit
  masks.cols[c] |= bit
  masks.boxes[boxIndex(r, c)] |= bit
}

const unplace = (masks: Masks, r: number, c: number, bit: number) => {
  masks.rows[r] ^= bit
  masks.cols[c] ^= bit
  masks.boxes[boxIndex(r, c)] ^= bit
}

const candidateMask = (masks: Masks, r: number, c: number) =>
  ALL & ~(masks.rows[r] | masks.cols[c] | masks.boxes[boxIndex(r, c)])

const shuffle = <T>(items: T[]): T[] => {
  const arr = items.slice()
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const swap = arr[i]
    arr[i] = arr[j]
    arr[j] = swap
  }
  return arr
}

const numbersFromMask = (mask: number): number[] => {
  const nums: number[] = []
  let bits = mask
  while (bits) {
    const bit = bits & -bits
    bits ^= bit
    nums.push((31 - Math.clz32(bit)) + 1)
  }
  return nums
}

/** 统计解的个数，达到 limit 即停止。 */
const countSolutions = (board: number[][], masks: Masks, limit: number): number => {
  let bestR = -1
  let bestC = -1
  let bestMask = 0
  let bestCount = 10

  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      if (board[r][c] !== 0) continue
      const mask = candidateMask(masks, r, c)
      const count = bitCount(mask)
      if (count === 0) return 0
      if (count < bestCount) {
        bestCount = count
        bestMask = mask
        bestR = r
        bestC = c
      }
    }
  }

  if (bestR < 0) return 1

  let found = 0
  for (const n of numbersFromMask(bestMask)) {
    const bit = 1 << (n - 1)
    board[bestR][bestC] = n
    place(masks, bestR, bestC, bit)
    found += countSolutions(board, masks, limit)
    unplace(masks, bestR, bestC, bit)
    board[bestR][bestC] = 0
    if (found >= limit) return found
  }
  return found
}

const fill = (board: number[][], masks: Masks, r = 0, c = 0): boolean => {
  if (r === 9) return true
  if (c === 9) return fill(board, masks, r + 1, 0)
  if (board[r][c] !== 0) return fill(board, masks, r, c + 1)

  for (const n of shuffle(numbersFromMask(candidateMask(masks, r, c)))) {
    const bit = 1 << (n - 1)
    board[r][c] = n
    place(masks, r, c, bit)
    if (fill(board, masks, r, c + 1)) return true
    unplace(masks, r, c, bit)
    board[r][c] = 0
  }
  return false
}

const solvedBoard = (): number[][] => {
  const board = Array.from({ length: 9 }, () => Array(9).fill(0))
  const masks = emptyMasks()

  for (let box = 0; box < 3; box++) {
    const nums = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9])
    let k = 0
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        const r = box * 3 + i
        const c = box * 3 + j
        const n = nums[k++]
        board[r][c] = n
        place(masks, r, c, 1 << (n - 1))
      }
    }
  }

  fill(board, masks)
  return board
}

/** 按难度生成一盘只有唯一解的题目，0 表示空格。 */
export const createPuzzle = (difficulty: DifficultyKey): number[][] => {
  const solved = solvedBoard()
  const board = solved.map((row) => row.slice())
  const masks = emptyMasks()
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      place(masks, r, c, 1 << (board[r][c] - 1))
    }
  }

  const holes = HOLES[difficulty]
  let removed = 0
  for (const idx of shuffle(Array.from({ length: 81 }, (_, i) => i))) {
    if (removed >= holes) break
    const r = Math.floor(idx / 9)
    const c = idx % 9
    const backup = board[r][c]
    if (backup === 0) continue
    const bit = 1 << (backup - 1)
    board[r][c] = 0
    unplace(masks, r, c, bit)
    if (countSolutions(board, masks, 2) === 1) {
      removed++
    } else {
      board[r][c] = backup
      place(masks, r, c, bit)
    }
  }
  return board
}
